// PATH: src/pages/EngagementPage.tsx — Phase 6 : usage Tilmicode/TilmidPlus par
// élève et par classe. Accès réservé direction/surveillant général (backend).
import React, { useCallback, useEffect, useState } from "react";
import {
  Button, Card, Form, InputNumber, Modal, Popconfirm, Result, Select, Space, Table, Tabs, Tag, Typography, message,
} from "antd";
import { LinkOutlined, WhatsAppOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useApiClient } from "../api/apiClients";
import { useSchoolApi } from "../api/school";
import { WHATSAPP_ENABLED } from "../config/features";
import { useTabParam } from "../hooks/useTabParam";
import type { SchoolYear } from "../models/school";

const fmt = (v?: string | null) => (v ? dayjs(v).format("DD/MM/YYYY") : "jamais");
const apiError = (err: any) => message.error(err?.response?.data?.error || "Opération refusée");

const EngagementPage: React.FC = () => {
  const http = useApiClient("tilmischool");
  const school = useSchoolApi();
  const [years, setYears] = useState<SchoolYear[]>([]);
  const [yearId, setYearId] = useState<number>();
  const [tab, setTab] = useTabParam("students");
  const [students, setStudents] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [forbidden, setForbidden] = useState(false);
  const [linking, setLinking] = useState<any | null>(null);

  useEffect(() => {
    school.listYears().then((ys) => { setYears(ys); if (ys.length) setYearId(ys[0].id); }).catch(() => undefined);
  }, [school]);

  const load = useCallback(() => {
    if (!yearId) return;
    http.get("/api/v1/school/engagement/students", { params: { school_year_id: yearId } })
      .then((r) => { setStudents(r.data ?? []); setForbidden(false); })
      .catch((err) => { if (err?.response?.status === 403) setForbidden(true); });
    http.get("/api/v1/school/engagement/classes", { params: { school_year_id: yearId } })
      .then((r) => setClasses(r.data ?? [])).catch(() => undefined);
  }, [http, yearId]);
  useEffect(load, [load]);

  if (forbidden) {
    return <Result status="403" title="Réservé à la direction et au surveillant général"
      subTitle="Données comportementales de comptes mineurs — accès restreint (Loi 09-08)." />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>Engagement Tilmicode / Tilmid+</Typography.Title>
        <Select style={{ width: 160 }} value={yearId} onChange={setYearId}
          options={years.map((y) => ({ value: y.id, label: y.label }))} placeholder="Année" />
        {WHATSAPP_ENABLED && (<>
          {/* « Inactif » = aucune trace depuis 14 jours, ni connexion, ni leçon
              Tilmicode, ni quête Tilmid+ — un élève qui travaille sans se
              reconnecter n'est plus relancé à tort. */}
          <Popconfirm title="Alerter les tuteurs des élèves sans aucune activité depuis 14 jours ?"
            onConfirm={() => http.post("/api/v1/school/engagement/inactivity-alerts",
              { school_year_id: yearId, days: 14 })
              .then((r) => message.success(`${r.data.sent} alerte(s) WhatsApp envoyée(s)`))
              .catch(apiError)}>
            <Button icon={<WhatsAppOutlined />} disabled={!yearId}>Alertes d'inactivité (14 j)</Button>
          </Popconfirm>
        </>)}
      </Space>

      <Tabs activeKey={tab} onChange={setTab} items={[
        {
          key: "students", label: "Par élève",
          children: (
            <Card>
              {/* En-têtes groupés : l'ACTIVITÉ de chaque application d'abord,
                  les connexions ensuite. L'ordre traduit leur fiabilité — le
                  cookie de session étant partagé par tout *.tilmicode.com, un
                  élève peut travailler des semaines sans ouvrir de session, et
                  ne compter que ses connexions le ferait passer pour absent. */}
              <Table rowKey="student_id" dataSource={students} pagination={false} size="small" scroll={{ x: 1100 }}
                columns={[
                  { title: "Élève", dataIndex: "student_name", fixed: "left", width: 180 },
                  { title: "Classe", dataIndex: "class_label", render: (v) => v || "—" },
                  { title: "Compte", render: (_, s: any) => s.has_account
                      ? <Tag color="green">#{s.userapp_id}</Tag>
                      : <Button size="small" icon={<LinkOutlined />} onClick={() => setLinking(s)}>Lier</Button> },
                  {
                    title: "Tilmicode",
                    children: [
                      { title: "Dern. activité", key: "tc_last", render: (_, s: any) => fmt(s.tilmicode_last_activity) },
                      { title: "Leçons 30 j", key: "tc_30", render: (_, s: any) =>
                          s.tilmicode_lessons_30d > 0 ? `${s.tilmicode_lessons_30d}` : "—" },
                      { title: "Progression", key: "tc_pct", render: (_, s: any) =>
                          s.tilmicode_lessons_total > 0
                            ? <Tag bordered={false}>{s.tilmicode_progress_pct} % · {s.tilmicode_lessons_total} leçons</Tag>
                            : "—" },
                      { title: "IA / Projets", key: "tc_extra", render: (_, s: any) =>
                          s.tilmicode_chat_30d > 0 || s.tilmicode_projects_total > 0
                            ? `${s.tilmicode_chat_30d} · ${s.tilmicode_projects_total}`
                            : "—" },
                    ],
                  },
                  {
                    title: "Tilmid+",
                    children: [
                      { title: "Dern. activité", key: "tp_last", render: (_, s: any) => fmt(s.tilmidplus_last_activity) },
                      { title: "30 j", key: "tp_30", render: (_, s: any) =>
                          s.tilmidplus_quests_30d > 0
                            ? `${s.tilmidplus_quests_30d} quêtes · ${Math.round(s.tilmidplus_minutes_30d)} min`
                            : "—" },
                    ],
                  },
                  {
                    title: "Connexions",
                    children: [
                      { title: "Dernière", key: "lg_last", render: (_, s: any) => fmt(s.last_login_any) },
                      { title: "7 j / 30 j", key: "lg_cnt", render: (_, s: any) => `${s.logins_7d} / ${s.logins_30d}` },
                    ],
                  },
                ]} />
            </Card>
          ),
        },
        {
          key: "classes", label: "Par classe",
          children: (
            <Card>
              <Table rowKey="class_label" dataSource={classes} pagination={false} size="small"
                columns={[
                  { title: "Classe", dataIndex: "class_label" },
                  { title: "Élèves", dataIndex: "student_count" },
                  { title: "Comptes liés", dataIndex: "linked_count" },
                  { title: "Actifs 7 j (Tilmicode)", dataIndex: "tilmicode_active_7d" },
                  { title: "Leçons 30 j (total)", dataIndex: "tilmicode_lessons_30d_total" },
                  { title: "Tilmid+ 30 j (total)", dataIndex: "tilmidplus_minutes_30d_total",
                    render: (v: number) => `${Math.round(v)} min` },
                  { title: "Connectés 7 j", dataIndex: "active_last_7d" },
                  { title: "Connexions 30 j (moy.)", dataIndex: "avg_logins_30d", render: (v: number) => v.toFixed(1) },
                ]} />
            </Card>
          ),
        },
      ]} />

      <Modal title={linking ? `Lier un compte à ${linking.student_name}` : ""} open={!!linking}
        onCancel={() => setLinking(null)} footer={null} destroyOnClose>
        <Typography.Paragraph type="secondary">
          Identifiant du compte tc_identity de l'élève (userapp_id). Les comptes élèves (MINOR) ne sont pas
          listables via tc_identity aujourd'hui — cf. PRE_PROD_CHECKLIST point 9.
        </Typography.Paragraph>
        <Form layout="inline"
          onFinish={(v) => http.put(`/api/v1/school/students/${linking!.student_id}/account`,
            { userapp_id: v.userapp_id })
            .then(() => { setLinking(null); load(); }).catch(apiError)}>
          <Form.Item name="userapp_id" rules={[{ required: true, message: "Identifiant du compte requis" }]}><InputNumber min={1} placeholder="userapp_id" /></Form.Item>
          <Button htmlType="submit" type="primary">Lier</Button>
        </Form>
      </Modal>
    </div>
  );
};

export default EngagementPage;
